import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown, Globe2 } from 'lucide-react'
import { useLanguage } from '@/localization'
import type { LanguageCode } from '../../../../../shared/localization/core'

export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { language, languageOption, languages, setLanguage, t } = useLanguage()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const selectLanguage = (code: LanguageCode) => {
    setLanguage(code)
    setOpen(false)
  }

  return (
    <div
      className={`relative ${compact ? 'w-full' : ''}`}
      ref={rootRef}
      data-no-translate
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t('Select language')}
        title={t('Select language')}
        className={`group inline-flex items-center gap-2 rounded-lg border-2 bg-slate-deep/80 px-3 py-1.5 font-pixel text-[10px] uppercase tracking-wider text-white shadow-block transition-all duration-200 cursor-pointer ${
          open
            ? 'border-grass shadow-[0_0_14px_rgba(74,124,42,0.25)]'
            : 'border-grass-dark/70 hover:border-grass'
        } ${compact ? 'w-full justify-between' : ''}`}
      >
        <span className="inline-flex items-center gap-2">
          <Globe2
            size={14}
            className={`transition-colors ${open ? 'text-grass' : 'text-grass group-hover:text-grass'}`}
            aria-hidden="true"
          />
          <span>{languageOption.label}</span>
        </span>
        <ChevronDown
          size={13}
          className={`text-white/45 transition-transform duration-200 ${open ? 'rotate-180 text-grass' : 'group-hover:text-white/70'}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={t('Select language')}
          className={`absolute z-[60] mt-2 min-w-[11.5rem] overflow-hidden rounded-xl border-2 border-grass-dark/60 bg-slate-deep p-1.5 shadow-block ${compact ? 'left-0 right-0 w-full' : 'right-0'}`}
        >
          <div className="px-2.5 pb-1.5 pt-1 font-pixel text-[9px] uppercase tracking-[0.22em] text-grass">
            {t('Language')}
          </div>

          <div className="space-y-0.5">
            {languages.map((item) => {
              const isActive = item.code === language

              return (
                <button
                  key={item.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => selectLanguage(item.code)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-150 cursor-pointer ${
                    isActive
                      ? 'bg-grass-dark/40 text-white ring-1 ring-grass/50'
                      : 'text-white/75 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span
                    className={`inline-flex min-w-[2rem] items-center justify-center rounded-md px-1.5 py-0.5 font-pixel text-[9px] tracking-wider ${
                      isActive
                        ? 'bg-grass/25 text-grass'
                        : 'bg-white/5 text-white/55'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className="flex-1 text-sm font-semibold tracking-wide">
                    {item.name}
                  </span>
                  {isActive && (
                    <Check size={14} className="shrink-0 text-grass" aria-hidden="true" />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
